import { useState } from 'react'
import {
  ArrowRight,
  ClipboardCheck,
  ExternalLink,
  Mail,
  MapPin,
  Package,
  PackageCheck,
  PackageOpen,
  Phone,
  Truck,
  Undo2,
  XCircle,
  type LucideIcon,
} from 'lucide-react'

import type {
  PortalContact,
  PortalDelivery,
  PortalItem,
  PortalOrder,
  PortalStage,
} from '@/api/client'
import { ProtectionPlans } from '@/components/portal/protection-plans'
import { WarrantyClaims } from '@/components/portal/warranty-claims'
import { Button } from '@/components/ui/button'
import {
  PROGRESS,
  STAGE_LABEL,
  deliveryDates,
  formatWeekday,
  orderHeadline,
  orderHeadlineNote,
  orderMeta,
  phoneHref,
  sortedDeliveries,
  stageCounts,
} from '@/lib/tracker'
import { cn } from '@/lib/utils'

const STAGE_ICON: Record<PortalStage, LucideIcon> = {
  ordered: ClipboardCheck,
  processing: PackageOpen,
  on_the_way: Truck,
  delivered: PackageCheck,
  cancelled: XCircle,
  returned: Undo2,
}

const STAGE_TONE: Record<PortalStage, string> = {
  ordered: 'text-foreground',
  processing: 'text-foreground',
  on_the_way: 'text-usa-blue',
  delivered: 'text-emerald-700',
  cancelled: 'text-rose-700',
  returned: 'text-rose-700',
}

// Items shown before "Show all": fewer when an order splits into many deliveries.
const COLLAPSED_ITEMS = 5
const COLLAPSED_ITEMS_MANY_DELIVERIES = 2

export function OrderTracker({
  order,
  token,
  headingLevel = 1,
}: {
  order: PortalOrder
  token?: string
  headingLevel?: 1 | 2
}) {
  const Heading = headingLevel === 1 ? 'h1' : 'h2'
  const shipTo = [order.ship_to_city, order.ship_to_state].filter(Boolean).join(', ')
  const note = orderHeadlineNote(order)
  const meta = orderMeta(order)
  const deliveries = sortedDeliveries(order)
  const several = deliveries.filter((d) => d.stage !== 'cancelled').length > 1

  return (
    <article className="space-y-8">
      <header>
        <Heading className="text-[2rem] leading-tight font-semibold tracking-tight text-balance sm:text-[2.5rem]">
          {orderHeadline(order)}
        </Heading>
        <p className="text-muted-foreground mt-2 text-[15px]">
          {note ? <span className="block">{note}</span> : null}
          {meta.join(' · ')}
          {shipTo ? <span className="block">Delivering to {shipTo}</span> : null}
        </p>
        {PROGRESS.includes(order.stage) ? <ProgressRail order={order} /> : null}
      </header>

      <div className="space-y-4">
        {deliveries.map((delivery, index) => (
          <DeliveryPanel
            key={index}
            delivery={delivery}
            label={several ? `Delivery ${index + 1} of ${deliveries.length}` : null}
            collapsedItems={deliveries.length > 3 ? COLLAPSED_ITEMS_MANY_DELIVERIES : COLLAPSED_ITEMS}
          />
        ))}
      </div>
      {token && order.protection.length > 0 ? (
        <ProtectionPlans token={token} units={order.protection} />
      ) : null}
      {token ? <WarrantyClaims token={token} order={order} /> : null}
    </article>
  )
}

function ProgressRail({ order }: { order: PortalOrder }) {
  const current = PROGRESS.indexOf(order.stage)
  const counts = stageCounts(order)
  return (
    <ol aria-label="Order progress" className="relative mt-8 grid grid-cols-4">
      <span aria-hidden="true" className="bg-border absolute top-[7px] right-[12.5%] left-[12.5%] h-0.5" />
      <span
        aria-hidden="true"
        className="bg-foreground animate-rail-fill absolute top-[7px] left-[12.5%] h-0.5 origin-left motion-reduce:animate-none"
        style={{ width: `${(current / (PROGRESS.length - 1)) * 75}%` }}
      />
      {PROGRESS.map((stage, index) => {
        const done = index < current
        const now = index === current
        const count = counts[stage]
        return (
          <li
            key={stage}
            aria-current={now ? 'step' : undefined}
            className="relative flex flex-col items-center text-center"
          >
            <span
              className={cn(
                'relative z-10 grid size-4 place-items-center rounded-full border-2',
                done && 'border-foreground bg-foreground',
                now && 'border-usa-blue bg-background ring-usa-blue/15 ring-4',
                !done && !now && 'border-border bg-background',
              )}
            >
              {now ? <span className="bg-usa-blue size-1.5 rounded-full" /> : null}
            </span>
            <span
              className={cn(
                'mt-2.5 text-xs leading-tight sm:text-sm',
                now ? 'text-foreground font-semibold' : done ? 'text-foreground' : 'text-muted-foreground',
              )}
            >
              {STAGE_LABEL[stage]}
            </span>
            {count ? (
              <span className="text-muted-foreground mt-0.5 text-xs tabular-nums">
                {count} {count === 1 ? 'item' : 'items'}
              </span>
            ) : null}
          </li>
        )
      })}
    </ol>
  )
}

function StageTag({ stage }: { stage: PortalStage }) {
  const Icon = STAGE_ICON[stage]
  return (
    <span className={cn('inline-flex items-center gap-2 font-semibold', STAGE_TONE[stage])}>
      <Icon aria-hidden="true" className="size-[18px]" />
      {STAGE_LABEL[stage]}
    </span>
  )
}

function DeliveryPanel({
  delivery,
  label,
  collapsedItems,
}: {
  delivery: PortalDelivery
  label: string | null
  collapsedItems: number
}) {
  const [expanded, setExpanded] = useState(false)
  const items = delivery.items
  const collapsible = items.length > collapsedItems + 1
  const visible = collapsible && !expanded ? items.slice(0, collapsedItems) : items
  const source = [
    delivery.brand ? `From ${delivery.brand}` : null,
    delivery.carrier,
    delivery.freight ? 'Freight delivery' : null,
  ]
    .filter(Boolean)
    .join(' · ')
  const dates = deliveryDates(delivery)
  return (
    <section className="bg-card overflow-hidden rounded-2xl border">
      <div className="flex flex-col gap-3 px-4 pt-4 sm:flex-row sm:items-start sm:justify-between sm:px-5">
        <div className="min-w-0">
          {label ? <p className="text-muted-foreground mb-1 text-xs">{label}</p> : null}
          <h2>
            <StageTag stage={delivery.stage} />
          </h2>
          {source ? <p className="text-muted-foreground mt-1 text-sm">{source}</p> : null}
          {delivery.latest_update ? (
            <p className="mt-2 inline-flex items-center gap-1.5 text-sm">
              <MapPin aria-hidden="true" className="text-usa-blue size-4 shrink-0" />
              <span>
                {[
                  delivery.latest_update.label,
                  delivery.latest_update.location,
                  delivery.latest_update.on ? formatWeekday(delivery.latest_update.on) : null,
                ]
                  .filter(Boolean)
                  .join(' · ')}
              </span>
            </p>
          ) : null}
        </div>
        {delivery.tracking.length > 0 ? (
          <div className="flex flex-wrap gap-2">
            {delivery.tracking.map((tracking) =>
              tracking.url ? (
                <Button key={tracking.number} asChild variant="outline" size="sm">
                  {/* eslint-disable-next-line no-restricted-syntax -- carrier tracking page */}
                  <a href={tracking.url} target="_blank" rel="noreferrer">
                    Track {tracking.number}
                    <ExternalLink aria-hidden="true" />
                  </a>
                </Button>
              ) : (
                <span key={tracking.number} className="text-muted-foreground text-sm">
                  Tracking {tracking.number}
                </span>
              ),
            )}
          </div>
        ) : null}
      </div>
      {dates.length > 0 ? (
        <dl className="mx-4 mt-4 flex items-stretch gap-3 sm:mx-5">
          {dates.map((date, index) => (
            <div key={date.label} className="contents">
              {index > 0 ? (
                <ArrowRight aria-hidden="true" className="text-muted-foreground mt-6 size-4 shrink-0" />
              ) : null}
              <div
                className={cn(
                  'min-w-0 flex-1 rounded-xl px-3 py-2.5',
                  date.estimated ? 'border border-dashed' : 'bg-muted',
                )}
              >
                <dt className="text-muted-foreground text-xs">
                  {date.label}
                  {date.estimated ? <span className="ml-1">· est.</span> : null}
                </dt>
                <dd className="mt-0.5 text-[15px] font-semibold tabular-nums">
                  {formatWeekday(date.day)}
                </dd>
              </div>
            </div>
          ))}
        </dl>
      ) : null}
      <ul className="mt-4 divide-y border-t">
        {visible.map((item, index) => (
          <ItemRow key={`${item.name}-${index}`} item={item} />
        ))}
      </ul>
      {collapsible ? (
        <button
          type="button"
          onClick={() => setExpanded((value) => !value)}
          aria-expanded={expanded}
          className="text-foreground hover:bg-muted/60 focus-visible:ring-ring/50 w-full border-t px-5 py-3 text-sm font-medium outline-none focus-visible:ring-[3px] focus-visible:ring-inset"
        >
          {expanded ? 'Show fewer' : `Show all ${items.length} items`}
        </button>
      ) : null}
    </section>
  )
}

function ItemRow({ item }: { item: PortalItem }) {
  return (
    <li className="grid grid-cols-[3.5rem_minmax(0,1fr)_auto] items-start gap-4 px-4 py-3.5 sm:px-5">
      <ItemImage url={item.image_url} />
      <div className="min-w-0">
        <p className="line-clamp-2 text-sm leading-snug font-medium">{item.name}</p>
        {item.model ? (
          <p className="text-muted-foreground mt-0.5 font-mono text-xs">{item.model}</p>
        ) : null}
        {item.options.length > 0 ? (
          <p className="text-muted-foreground mt-1 text-xs">With {item.options.join(', ')}</p>
        ) : null}
      </div>
      <p className="text-sm tabular-nums">
        <span className="sr-only">Quantity </span>
        <span aria-hidden="true" className="text-muted-foreground">
          ×
        </span>
        {item.quantity}
      </p>
    </li>
  )
}

function ItemImage({ url }: { url: string | null }) {
  const [failed, setFailed] = useState(false)
  if (!url || failed) {
    return (
      <span className="bg-muted grid size-14 place-items-center rounded-lg">
        <Package aria-hidden="true" className="text-muted-foreground size-5" />
      </span>
    )
  }
  return (
    <img
      alt=""
      src={url}
      loading="lazy"
      onError={() => setFailed(true)}
      className="size-14 rounded-lg border bg-white object-contain p-1"
    />
  )
}

export function ContactPanel({ contact }: { contact: PortalContact }) {
  return (
    <section className="bg-card rounded-2xl border p-5">
      <h2 className="font-semibold">Questions about your order?</h2>
      {contact.name ? <p className="text-muted-foreground mt-1 text-sm">{contact.name}</p> : null}
      <div className="mt-4 grid gap-2">
        {contact.email ? (
          <Button asChild variant="outline" className="min-w-0 justify-start">
            {/* eslint-disable-next-line no-restricted-syntax -- mailto link */}
            <a href={`mailto:${contact.email}`}>
              <Mail aria-hidden="true" />
              <span className="min-w-0 truncate">{contact.email}</span>
            </a>
          </Button>
        ) : null}
        {contact.phone ? (
          <Button asChild variant="outline" className="justify-start">
            {/* eslint-disable-next-line no-restricted-syntax -- phone link */}
            <a href={phoneHref(contact.phone)}>
              <Phone aria-hidden="true" />
              {contact.phone}
            </a>
          </Button>
        ) : null}
      </div>
    </section>
  )
}
